import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import api from "../../axios/api";
import { showNotification } from "../../redux/slices/notificationSlice";
import { Controller, useForm } from "react-hook-form";
import { FormControl, MenuItem, Select, TextField } from "@mui/material";
import CustomButton from "../../componentsII/Buttons"
import ConfirmationModal from '../../componentsII/ConfirmationModal';
import CustomCard from '../../componentsII/Card';

const IndustrialGasConsumptionDetailModal = ({
  detailModalData: initialData,
  refetch,
  handleCloseMainDetailModal,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state);
  const year = useSelector((state) => state.user.selectedYear);

  const [detailModalData, setDetailModalData] = useState({ ...initialData });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [gases, setGases] = useState([]);
  const [fuelUnit, setFuelUnit] = useState([]);

  const schema = Yup.object().shape({
    qty_unit_id: Yup.number().required("Unit is required").test('not-zero', 'Unit is required', value => value !== 0),
    ind_gas_id: Yup.number().required("Fuel is required").test('not-zero', 'Fuel is required', value => value !== 0),
    fuel_qty: Yup.number().required("Fuel quantity is required"),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: detailModalData?.id,
      comp_equipment_id: detailModalData?.value,
      qty_unit_id: detailModalData?.qty?.qty_id || 0,
      ind_gas_id: detailModalData?.fuel?.ind_gas_id || 0,
      fuel_qty: detailModalData?.fuel_qty || null,
      year: year.start_year,
    },
  });

  const getGas = async () => {
    try {
      const res = await api.get(`/consumption/indGasList`);
      setGases(res.data.data);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  const getFuelUnit = async () => {
    try {
      const res = await api.get(`/common/measurementType?type_code=VOLUME`);
      setFuelUnit(res.data.data);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  useEffect(() => {
    getGas();
    getFuelUnit();
  }, []);

  const handleDelete = async () => {
    try {
      const response = await api.delete(
        `/consumption/deleteIndustrialGasConsumption/${detailModalData?.id}`
      );
      if (response.data) {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
        console.log(response.data.message);
      }
      refetch();
      handleCloseMainDetailModal();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsDeleteModalOpen(false);
  };

  const handleEditSubmit = async (data) => {
    const body = { ...data,year:year.start_year };
    try {
      const response = await api.post(
        "/consumption/addUpdateIndustrialGasConsumption",
        body
      );
      if (response.data.status === "200") {
        dispatch(
          showNotification({ message: response.data.message, type: "success" })
        );
        refetch();
        handleCloseMainDetailModal();
      } else {
        dispatch(
          showNotification({ message: response.data.message, type: "error" })
        );
        setIsEditable(false);
        console.log(response.data.message);
      }
      console.log("Form submitted successfully:", response);
    } catch (error) {
      dispatch(
        showNotification({
          message: error.response.data.message,
          type: "error",
        })
      );
      console.error("Error submitting form:", error);
    }
  };

  return (
    <div>
      <div>
        <div className="flex border-b border-dashed border-gray-500 ">
          <div className="px-8 w-full text-center">
            <h1 className="text-2xl font-bold text-green-600">
              {detailModalData?.id ? "Edit" : "Add"}{" "}
              {user?.selectedYear?.start_year} Industrial Gas consumption
            </h1>
            <p className="text-gray-500 mb-5 mt-1 text-md font-medium px-10">
              To calculate emission for this equipment, we'll gather the amount
              of fuel this equipment used during {user?.selectedYear?.start_year}
            </p>
          </div>
        </div>

        <div className="px-6">
          <div className="flex justify-center w-full items-center py-6">
            <div className="w-[45%] rounded-[20px] border-[10px] shadow-md">
              <CustomCard
                image={detailModalData.img}
                title={detailModalData.label}
                subtitleI={`${detailModalData?.equipment_type_details?.equipment_type_name}`}
                subtitleII={
                  detailModalData?.comp_location_details?.comp_location_name
                }
              />
            </div>
          </div>
          <form onSubmit={handleSubmit(handleEditSubmit)}>
            <div className="grid grid-cols-1 py-3">
              <CompSelect
                title="What kind of industrial gas does this equipment used ?"
                name="ind_gas_id"
                isError={!!errors.ind_gas_id}
                errorText={errors.ind_gas_id?.message}
                control={control}
                options={gases}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <CompInput
                type="number"
                title={`How much gas did this vehicle used in ${year?.start_year} ?`}
                name="fuel_qty"
                isError={!!errors.fuel_qty}
                errorText={errors.fuel_qty?.message}
                control={control}
              />
              <CompSelect
                title="Unit of indurial gas"
                name="qty_unit_id"
                isError={!!errors.qty_unit_id}
                errorText={errors.qty_unit_id?.message}
                control={control}
                options={fuelUnit}
              />
            </div>
            <div className="flex justify-between items-center w-full py-2">
              {detailModalData?.id ? (
                <div>
                  <CustomButton
                    className="bg-red-500 hover:bg-red-700 text-white "
                    click={() => setIsDeleteModalOpen(true)}
                    text="Delete"
                  />
                </div>
              ) : (
                <div></div>
              )}

              <div className="w-full flex justify-end gap-5 items-center py-5">
                <CustomButton
                  className="bg-gray-500 hover:bg-gray-700 text-white "
                  click={() => handleCloseMainDetailModal(false)}
                  text="Cancel"
                />
                <CustomButton
                  className="bg-green-600 hover:bg-green-700 text-white "
                  type="submit"
                  text="Submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        cancelText="Cancel"
        confirmText="Delete"
        onConfirmClick={handleDelete}
        onCancelClick={() => setIsDeleteModalOpen(false)}
        text={
          <>
            <p>Are you sure you want to delete this item?</p>
            <p>This action cannot be undone.</p>
          </>
        }
        header={<div className="text-red-500">Delete Vehicle Usage</div>}
      />
    </div>
  );
};

export default IndustrialGasConsumptionDetailModal;


const CompInput = ({
    title,
    control,
    isError,
    errorText,
    name,
    required = true,
    type="text"
  }) => {
    return (
      <div className={`h-16 w-full items-center`}>
        <div className="font-medium text-gray-900 flex gap-1 ">
          {title}
          <p className={`${required ? "text-red-500" : "invisible"}`}>*</p>
        </div>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <TextField
              type={type}
              {...field}
              fullWidth
              error={isError}
              helperText={errorText}
              placeholder="Enter Value"
              margin="none"
              InputProps={{
                disableUnderline: true,
                sx: {
                  padding: 0,
                  "& .MuiInputBase-input": {
                    padding: "10px",
                  },
                },
              }}
              sx={{
                padding: 0,
                margin: 0,
              }}
              onBlur={(e) => {
                if(type == "number") {
                  const formattedValue = parseFloat(e.target.value).toFixed(2);
                  field.onChange(formattedValue);
                }
              }}
            />
          )}
        />
      </div>
    );
  };
  
  const CompSelect = ({
    title,
    control,
    isError,
    errorText,
    name,
    options,
    value,
    required = true,
    handleChange = () => {},
  }) => {
    return (
      <div className={`h-16 w-full items-center`}>
        <div className="font-medium text-gray-900 flex gap-1 ">
          {title}
          <p className={`${required ? "text-red-500" : "invisible"}`}>*</p>
        </div>
        <div className="">
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={isError}>
                <Select
                  value={value}
                  {...field}
                  sx={{
                    padding: 0, // Removes padding in the Select component itself
                    "& .MuiInputBase-input": {
                      padding: "10px !important", // Removes padding from the input area
                    },
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleChange(e.target.value);
                  }}
                  renderValue={(selected) => {
                    const selectedVal = options.find((item) => item?.value == selected)
                    if (selectedVal == undefined) {
                      return <em>Select option</em>;
                    }
                    return selectedVal?.label;
                  }}
                >
                  <MenuItem key={1100000} value="" disabled>
                    Select option
                  </MenuItem>
                  {options.map((make) => (
                    <MenuItem key={make.value} value={make.value}>
                      {make.label}
                    </MenuItem>
                  ))}
                </Select>
                {errorText && <p className="text-xs text-red-500">{errorText}</p>}
              </FormControl>
            )}
          />
        </div>
      </div>
    );
  };
  
